'use client';

import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useCandidateNavigation, type CandidateSearchFilters } from '@components/contexts/CandidateNavigationContext';
import { useSwrJobListCardsInfinite } from '@hooks/useSwrApi/endpoints/job/useSwrJobListCardsInfinite';
import { buildSearchParams } from '@utils/data-fetching/searchParams';

export interface EmptyJobListFallbackProps {
  locale: Locale;
  candidateSearch?: CandidateSearchFilters;
  /** Backend-decomposed filters from the empty `/v2/jobs` response (or sidebar view).
   *  `decomposedFilters.keyword` is the backend's already-cleaned keyword (no location/salary
   *  tokens) — used directly as the relaxed-search keyword. */
  decomposedFilters?: ApiSchema<'JobSearchRequestFilter'>;
}

/** Builds the relaxed candidate-search view for a specific field. Returns `undefined` when
 *  the field isn't actually constrained (nothing to relax). Uses `decomposedFilters.keyword`
 *  (already cleaned by the backend) so no manual token-stripping is needed. */
const buildRelaxedView = (
  decomposedFilters: ApiSchema<'JobSearchRequestFilter'> | undefined,
  candidateSearch: CandidateSearchFilters | undefined,
  field: 'location' | 'salary',
): CandidateSearchFilters | undefined => {
  if (!decomposedFilters) return undefined;
  const { keyword } = decomposedFilters;
  if (field === 'location') {
    if (!decomposedFilters.locations?.length) return undefined;
    // The keyword-only URL model means salary cannot be preserved separately when
    // relaxing location — we use the backend's cleaned keyword as-is.
    return keyword ? { keyword } : undefined;
  }
  if (decomposedFilters.minSalary == null) return undefined;
  const locations = candidateSearch?.locations?.length
    ? candidateSearch.locations
    : decomposedFilters.locations;
  return {
    ...(keyword && { keyword }),
    ...(locations?.length && { locations }),
  };
};

/** Serializes a relaxed view to the same `urlParams` shape `useSwrJobListCardsInfinite` consumes. */
const buildUrlParams = (view: CandidateSearchFilters): string => {
  const params: Record<string, string | string[]> = {};
  if (view.keyword) params.keyword = view.keyword;
  if (view.locations?.length) params.location = view.locations;
  return buildSearchParams(params, '&', false);
};

export const EmptyJobListFallback = (props: EmptyJobListFallbackProps) => {
  const { locale, candidateSearch, decomposedFilters } = props;
  const { t } = useTranslation('common');
  const { navigateToCandidateSearch } = useCandidateNavigation();

  const withoutLocation = useMemo(
    () => buildRelaxedView(decomposedFilters, candidateSearch, 'location'),
    [decomposedFilters, candidateSearch],
  );
  const withoutSalary = useMemo(
    () => buildRelaxedView(decomposedFilters, candidateSearch, 'salary'),
    [decomposedFilters, candidateSearch],
  );

  // Priority across the file: `withoutLocation ?? withoutSalary` — relax location first,
  // fall back to salary, never both.
  const probeUrlParams = useMemo(() => {
    const relaxed = withoutLocation ?? withoutSalary;
    return relaxed ? buildUrlParams(relaxed) : '';
  }, [withoutLocation, withoutSalary]);

  const probe = useSwrJobListCardsInfinite({
    locale,
    limit: 1,
    urlParams: probeUrlParams,
    initialCursor: '',
    shouldFetch: !!probeUrlParams,
    // Anonymous: this is a background "are there results if we relax a filter?" check, not a
    // search the user ran — omit the session cookie so the backend doesn't save/process it.
    fetcherConfig: { credentials: 'omit' },
  });
  const probeHasResults = (probe.data?.[0]?.items?.length ?? 0) > 0;

  const handleFallbackClick = () => {
    const relaxed = withoutLocation ?? withoutSalary;
    if (relaxed) navigateToCandidateSearch(relaxed);
  };

  if (!probeHasResults) return null;

  return (
    <p className="mx-auto mt-4 w-fit max-w-full rounded-2xl border border-input bg-surface px-6 py-4 text-center text-sm text-ink shadow-sm">
      {withoutLocation
        ? t('job.list.intercept.fallback.location.intro')
        : t('job.list.intercept.fallback.salary.intro')}{' '}
      <button
        type="button"
        onClick={handleFallbackClick}
        className="font-medium text-primary underline-offset-2 hover:underline"
      >
        {withoutLocation
          ? t('job.list.intercept.fallback.location.link')
          : t('job.list.intercept.fallback.salary.link')}
      </button>
    </p>
  );
};
